import axios from 'axios';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import AuthLayout from './AuthLayout';
import './AuthLayout.css';

const AuthLogin = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);
    setResendMessage('');

    try {
      const response = await axios.post(`${API_URL}/users/login`, formData);
      
      localStorage.setItem('token', response.data.token);
      onLogin(response.data.user);
      
      // Redirect based on user role
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else if (response.data.user.role === 'client') {
        navigate('/dashboard');
      } else {
        // Respondents go directly to profiler
        navigate('/survey-profiler/all');
      }
    } catch (error) {
      const errorData = error.response?.data;

      if (errorData?.needsVerification) {
        setNeedsVerification(true);
        setUnverifiedEmail(errorData.email || formData.email);
        setError(errorData.message);
      } else {
        setError(errorData?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail || !canResend) return;

    setResending(true);
    setResendMessage('');

    try {
      const response = await axios.post(`${API_URL}/users/resend-verification`, {
        email: unverifiedEmail
      });

      setResendMessage(response.data.message);

      // Start 1-minute timer
      setCanResend(false);
      setResendTimer(60);
    } catch (error) {
      setResendMessage(error.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  // Timer effect for resend cooldown
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && !canResend) {
      setCanResend(true);
    }
  }, [resendTimer, canResend]);

  return (
    <AuthLayout>
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Sign in to your Dive-in Panels account</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="alert alert-danger">
              {error}
              {needsVerification && (
                <div className="verification-help" style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                    Haven't received the verification email or link expired?
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="btn btn-sm btn-warning"
                    disabled={resending || !canResend}
                    style={{ fontSize: '13px', padding: '6px 12px' }}
                  >
                    {resending ? 'Sending...' : !canResend ? `Resend in ${resendTimer}s` : 'Resend Verification Email'}
                  </button>
                  {resendMessage && (
                    <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: resendMessage.includes('sent') ? '#28a745' : '#dc3545' }}>
                      {resendMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <Link to="/auth/forgot-password" className="forgot-password">
                Forgot your password?
              </Link>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-signup"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
        
        <div className="login-footer">
          <p>
            Don't have an account? 
            <Link to="/auth/register" className="register-link">
              Join Now
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AuthLogin;
